import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Divider, IconButton, MenuItem, Tooltip, Typography, CircularProgress, Stack, Avatar, Alert,
  Chip, Button, Switch, FormControlLabel,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Close as CloseIcon,
  CheckCircle as VerifiedIcon,
  Cancel as UnverifiedIcon,
  Domain as OrgIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { PageHeader } from '../../components/shared/PageHeader'
import { PageActionButtons } from '../../components/shared/PageActionButtons'
import { DataTable } from '../../components/ui/DataTable'
import {
  EntityFormSection,
  EntityDrawerInput,
  EntityDrawerSelect,
  EntityFormField,
} from '../../components/shared/EntityFormDrawer'
import { DeleteDialog } from '../../components/shared/DeleteDialog'
import { AdminUser, CreateUserPayload, UpdateUserPayload, usersAdminApi, UserStatus } from '../../lib/api/usersAdminApi'
import { themeColors } from '../../app/layout/theme'
import { FormDialog, FormSelect, SearchBar } from '../../components/ui'
import { authStore } from '../../lib/stores/authStore'
import { lobsApi, orgsApi } from '../../lib/api/tenantsApi'
import { rolesApi, Role } from '../../lib/api/rolesApi'

const STATUS_COLORS: Record<UserStatus, { bg: string; color: string }> = {
  Active: { bg: themeColors.successLight, color: themeColors.success },
  Inactive: { bg: '#f0f0f0', color: themeColors.text.secondary },
  Suspended: { bg: themeColors.errorLight, color: themeColors.error },
}

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map((n) => n[0]?.toUpperCase() ?? '').join('')
}

// ── User Form Dialog ──────────────────────────────────────────────────────────
interface UserFormDialogProps {
  open: boolean
  user?: AdminUser | null
  roles: Role[]
  onClose: () => void
  onSave: (payload: CreateUserPayload | UpdateUserPayload) => Promise<void>
  isSaving: boolean
  isSuperAdmin: boolean
  callerOrgId: string
  callerOrgName: string | null
}

function UserFormDialog({ open, user, roles, onClose, onSave, isSaving, isSuperAdmin, callerOrgId, callerOrgName }: UserFormDialogProps) {
  const isEdit = !!user

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [roleId, setRoleId] = useState('')
  const [status, setStatus] = useState<UserStatus>('Active')
  const [orgId, setOrgId] = useState('')
  const [lobId, setLobId] = useState('')
  const [czentrixAgentId, setCzentrixAgentId] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const orgChangedByUser = useRef(false)
  const lobChangedByUser = useRef(false)

  const { data: orgs = [], isLoading: loadingOrgs } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
    enabled: open && isSuperAdmin,
    staleTime: 60_000,
  })

  const { data: lobs = [], isLoading: loadingLobs } = useQuery({
    queryKey: ['admin-lobs', orgId],
    queryFn: () => lobsApi.getAll(orgId),
    enabled: open && !!orgId,
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!orgChangedByUser.current) return
    orgChangedByUser.current = false
    setLobId('')
  }, [orgId])

  useMemo(() => {
    if (!open) return
    setName(user?.name ?? '')
    setEmail(user?.email ?? '')
    setPhone(user?.phone ?? '')
    setRoleId(user?.roleId ?? '')
    setStatus(user?.status ?? 'Active')
    setOrgId(user?.orgId ?? (isSuperAdmin ? '' : callerOrgId))
    setLobId(user?.lobId ?? '')
    setCzentrixAgentId(user?.czentrixAgentId != null ? String(user.czentrixAgentId) : '')
    setIsActive(user?.isActive ?? true)
    setErrors({})
    orgChangedByUser.current = false
    lobChangedByUser.current = false
  }, [open, user, isSuperAdmin, callerOrgId])

  const validate = () => {
    const e: Record<string, string> = {}
    if (!name.trim()) e.name = 'Name is required'
    if (!roleId) e.roleId = 'Role is required'
    if (!isEdit) {
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email'
      if (!orgId) e.orgId = 'Organisation is required'
      if (!lobId) e.lobId = 'Line of Business is required'
    }
    return e
  }

  const handleSave = async () => {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }

    const parsedAgentId = czentrixAgentId.trim() ? parseInt(czentrixAgentId.trim(), 10) : null
    const payload: any = {
      name: name.trim(),
      phone: phone.trim() || undefined,
      status,
      roleId,
      orgId: orgId || undefined,
      lobId: lobId || undefined,
      warehouseId: null,
      czentrixAgentId: parsedAgentId,
      isActive,
    }
    if (!isEdit) {
      payload.email = email.trim().toLowerCase()
    }
    await onSave(payload)
  }

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSave={handleSave}
      title={isEdit ? 'Edit User' : 'Create User'}
      saveLabel={isEdit ? 'Save Changes' : 'Create User'}
      saving={isSaving}
      maxWidth="sm"
    >
      <EntityFormSection columns={1}>
        <EntityDrawerInput
          label="Full Name"
          required
          value={name}
          onChange={(v) => { setName(v); if (errors.name) setErrors((p) => ({ ...p, name: '' })) }}
          error={errors.name}
        />
        <EntityDrawerInput
          label="Email Address"
          required
          value={email}
          onChange={(v) => { setEmail(v); if (errors.email) setErrors((p) => ({ ...p, email: '' })) }}
          error={errors.email}
          disabled={isEdit}
          fieldType="email"
        />
        <EntityDrawerInput
          label="Phone (optional)"
          value={phone}
          onChange={(v) => setPhone(v)}
          placeholder="+91 98765 43210"
          fieldType="phone"
        />

        <EntityDrawerInput
          label="C-Zentrix Agent ID (optional)"
          value={czentrixAgentId}
          onChange={(v) => {
            // Allow only numeric input
            if (v === '' || /^\d+$/.test(v)) setCzentrixAgentId(v)
          }}
          placeholder="e.g. 377"
          helperText="Set the agent's numeric ID from the C-Zentrix portal. Required for click-to-dial."
        />

        <EntityDrawerSelect
          label="Role"
          required
          value={roleId}
          onChange={(v) => { setRoleId(v); if (errors.roleId) setErrors((p) => ({ ...p, roleId: '' })) }}
          options={roles.map(r => ({
            label: r.name,
            value: r.id,
            subtitle: r.description
          }))}
          error={errors.roleId}
        />

        {isEdit && (
          <EntityDrawerSelect
            label="Status"
            required
            value={status}
            onChange={(v) => setStatus(v as UserStatus)}
            options={[
              { label: 'Active', value: 'Active' },
              { label: 'Inactive', value: 'Inactive' },
              { label: 'Suspended', value: 'Suspended' },
            ]}
          />
        )}

        <Box>
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>Eligible for lead assignment</Typography>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary }}>
                  Inactive users are skipped by Round Robin / Least Loaded / Team assignment, including the Workflow engine's Auto Assign action.
                </Typography>
              </Box>
            }
          />
        </Box>

        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <OrgIcon sx={{ fontSize: 14, color: themeColors.text.muted }} />
              <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Tenant Assignment
              </Typography>
            </Box>
          </Divider>

          <EntityFormSection columns={1}>
            {isSuperAdmin && orgs.length === 0 && !loadingOrgs && (
              <Alert severity="warning" sx={{ borderRadius: '8px', fontSize: '12px', mb: 1 }}>
                No organisations found.{' '}
                <strong><a href="/admin/tenants" style={{ color: 'inherit' }}>Create one first →</a></strong>
              </Alert>
            )}

            {isSuperAdmin ? (
              <EntityDrawerSelect
                label="Organisation"
                required={!isEdit}
                value={orgId}
                onChange={(v) => {
                  orgChangedByUser.current = true
                  setOrgId(v)
                  setErrors(p => ({ ...p, orgId: '' }))
                }}
                disabled={loadingOrgs}
                options={[
                  ...(isEdit ? [{ label: '— unchanged —', value: '' }] : []),
                  ...orgs.map(o => ({ label: o.name, value: o.id }))
                ]}
                error={errors.orgId}
              />
            ) : (
              <EntityFormField label="Organisation">
                <Typography fontSize="13px" fontWeight={600} color={callerOrgName ? 'text.primary' : 'text.secondary'}>
                  {callerOrgName ?? callerOrgId}
                </Typography>
                <Typography fontSize="11px" color="text.secondary" mt={0.25}>
                  Your organisation is set automatically
                </Typography>
              </EntityFormField>
            )}

            <EntityDrawerSelect
              label="Line of Business"
              required={!isEdit}
              value={lobId}
              onChange={(v) => {
                lobChangedByUser.current = true
                setLobId(v)
                setErrors(p => ({ ...p, lobId: '' }))
              }}
              disabled={!orgId || loadingLobs}
              options={[
                ...(isEdit ? [{ label: '— unchanged —', value: '' }] : []),
                ...lobs.map(l => ({ label: l.name, value: l.id }))
              ]}
              error={errors.lobId || (orgId && lobs.length === 0 && !loadingLobs ? 'No LOBs found' : undefined)}
            />

          </EntityFormSection>
        </Box>
      </EntityFormSection>
    </FormDialog>
  )
}

const STATUS_FILTER_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Suspended', value: 'Suspended' },
]

// ── Main Page ─────────────────────────────────────────────────────────────────
const UsersPage = observer(function UsersPage() {
  const qc = useQueryClient()

  const callerRole = authStore.user?.roleName ?? ''
  const isSuperAdmin = callerRole === 'SUPER_ADMIN'
  const canAdminister = isSuperAdmin || callerRole.toUpperCase().includes('ADMIN')
  const callerOrgId = authStore.user?.orgId ?? ''
  const callerOrgName = authStore.user?.orgName ?? null

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null)

  const [pageSizeTable, setPageSizeTable] = useState(10)

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['admin-users', page, search, roleFilter, statusFilter, pageSizeTable],
    queryFn: () => usersAdminApi.getAll({ page, pageSize: pageSizeTable, search, roleId: roleFilter || undefined, status: statusFilter || undefined }),
  })

  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: rolesApi.getAll,
  })

  const createMutation = useMutation({
    mutationFn: (payload: CreateUserPayload) => usersAdminApi.create(payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setFormOpen(false); toast.success('User created') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create user'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserPayload }) =>
      usersAdminApi.update(id, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setFormOpen(false); setEditingUser(null); toast.success('User updated') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update user'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersAdminApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setDeletingUser(null); toast.success('User deleted') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete user'),
  })

  // Quick toggle for "eligible for lead assignment" — sends name/phone along so the
  // PUT doesn't accidentally clear them (UserUpdateDto requires Name in the body).
  const toggleActiveMutation = useMutation({
    mutationFn: ({ row, isActive }: { row: AdminUser; isActive: boolean }) =>
      usersAdminApi.update(row.id, { name: row.name, phone: row.phone ?? undefined, isActive }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); toast.success('User updated') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update user'),
  })

  const handleSave = async (payload: CreateUserPayload | UpdateUserPayload) => {
    if (editingUser) {
      await updateMutation.mutateAsync({ id: editingUser.id, payload: payload as UpdateUserPayload })
    } else {
      await createMutation.mutateAsync(payload as CreateUserPayload)
    }
  }

  const users = usersData?.items ?? []
  const total = usersData?.total ?? 0

  const columns = [
    {
      id: 'name',
      label: 'User',
      render: (_: unknown, row: AdminUser) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 30, height: 30, fontSize: '12px', fontWeight: 700, background: themeColors.primary }}>
            {getInitials(row.name)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 500, color: themeColors.text.link }}>{row.name}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: themeColors.text.secondary }}>{row.email}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'role',
      label: 'Role',
      render: (_: unknown, row: AdminUser) => (
        <Chip label={row.roleName} size="small" sx={{ height: 22, fontSize: '11.5px', fontWeight: 600, bgcolor: `${themeColors.primary}15`, color: themeColors.primary }} />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (_: unknown, row: AdminUser) => {
        const sc = STATUS_COLORS[row.status] ?? { bg: '#f0f0f0', color: '#757575' }
        return (
          <Chip label={row.status} size="small" sx={{ height: 22, fontSize: '11.5px', fontWeight: 600, bgcolor: sc.bg, color: sc.color }} />
        )
      },
    },
    {
      id: 'isEmailVerified',
      label: 'Verified',
      render: (_: unknown, row: AdminUser) => row.isEmailVerified
        ? <VerifiedIcon sx={{ fontSize: 18, color: themeColors.success }} />
        : <UnverifiedIcon sx={{ fontSize: 18, color: themeColors.text.muted }} />,
    },
    {
      id: 'isActive',
      label: 'Assignable',
      render: (_: unknown, row: AdminUser) => (
        <Tooltip title={row.isActive ? 'Eligible for new lead assignments — click to deactivate' : 'Excluded from lead assignment — click to activate'}>
          <span>
            <Switch
              size="small"
              checked={row.isActive}
              disabled={!canAdminister || toggleActiveMutation.isPending}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => toggleActiveMutation.mutate({ row, isActive: e.target.checked })}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      render: (_: unknown, row: AdminUser) => new Date(row.createdAt).toLocaleDateString(),
    },
    ...(canAdminister ? [{
      id: 'actions',
      label: '',
      sortable: false,
      render: (_: unknown, row: AdminUser) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit user">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingUser(row); setFormOpen(true) }}
              sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#F2EEF9' } }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete user">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeletingUser(row) }}
              sx={{ color: themeColors.text.secondary, '&:hover': { color: themeColors.error } }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }] : []),
  ]

  return (
    <Box>
      <PageHeader
        title="User Management"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb
        actions={
          canAdminister ? (
            <PageActionButtons
              onBulkUpload={() => toast.info('Bulk upload coming soon')}
              onNew={() => { setEditingUser(null); setFormOpen(true) }}
              newLabel="New User"
            />
          ) : undefined
        }
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'center', flexWrap: 'nowrap' }}>
          <SearchBar
            placeholder="Search name or email..."
            value={search}
            onChange={(v) => { setSearch(v); setPage(1) }}
            sx={{ width: 280, flexShrink: 0 }}
          />

          <FormSelect
            label="Role"
            value={[{ label: 'All Roles', value: '' }, ...roles.map(r => ({ label: r.name, value: r.id }))].find(o => o.value === roleFilter) ?? null}
            onChange={(v) => { setRoleFilter((v as any)?.value ?? ''); setPage(1) }}
            options={[
              { label: 'All Roles', value: '' },
              ...roles.map((r) => ({ label: r.name, value: r.id }))
            ]}
            sx={{ width: 180, flexShrink: 0 }}
          />

          <FormSelect
            label="Status"
            value={STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter) ?? null}
            onChange={(v) => { setStatusFilter((v as any)?.value ?? ''); setPage(1) }}
            options={STATUS_FILTER_OPTIONS}
            sx={{ width: 160, flexShrink: 0 }}
          />

          {(search || roleFilter || statusFilter) && (
            <Button size="small" onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter(''); setPage(1) }}
              sx={{ color: themeColors.text.secondary, fontSize: '12px', flexShrink: 0 }}>
              Clear filters
            </Button>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={users}
              emptyMessage="No users found"
              emptyIcon={<PeopleIcon />}
              selectable={false}
            />

            {usersData && usersData.totalPages > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, px: 1 }}>
                <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
                  Showing {((page - 1) * pageSizeTable) + 1}–{Math.min(page * pageSizeTable, total)} of {total} users
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                    sx={{ borderRadius: '8px', minWidth: 80 }}>
                    Previous
                  </Button>
                  <Button size="small" variant="outlined" disabled={page >= usersData.totalPages} onClick={() => setPage((p) => p + 1)}
                    sx={{ borderRadius: '8px', minWidth: 80 }}>
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      <UserFormDialog
        open={formOpen}
        user={editingUser}
        roles={roles}
        onClose={() => { setFormOpen(false); setEditingUser(null) }}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
        isSuperAdmin={isSuperAdmin}
        callerOrgId={callerOrgId}
        callerOrgName={callerOrgName}
      />

      <DeleteDialog
        open={!!deletingUser}
        title="Delete User"
        name={deletingUser?.name}
        onClose={() => setDeletingUser(null)}
        onConfirm={() => deletingUser && deleteMutation.mutate(deletingUser.id)}
        isDeleting={deleteMutation.isPending}
      />
    </Box>
  )
})

export default UsersPage
